import {addYears, format, parseISO, subYears} from "date-fns";
import {useLanguageStore} from "@/stores/Language";

/**
 * Utility class for date formatting and manipulation.
 * Provides static methods to format dates and times in various standard formats, often used in database and UI contexts.
 */
abstract class DateFormatter {
    /*----- Date and time format patterns -----*/
    /** Date format: `yyyy-MM-dd` (year, month, day) */
    static yearMonthDayDbFormat = "yyyy-MM-dd";
    /** Date format: `yyyy-MM` (year and month) */
    static yearMonthDbFormat = "yyyy-MM";
    /** Date format: `MMMM yyyy` (full month name and year) */
    static monthYearFormat = "MMMM yyyy";
    /** Time format: `HH:mm:ss` (hours, minutes, seconds) */
    static hourMinuteSecondFormat = "HH:mm:ss";

    /** Time format: `HH-mm-ss` (hours, minutes and seconds) */
    static hourMinuteSecondHyphenFormat = "HH-mm-ss";

    /** Time format: `HH:mm` (hours and minutes) */
    static hourMinuteFormat = "HH:mm";


    /*----- static methods -----*/
    /**
     * Formats a date to a readable "Month Year" format.
     * @param date - The date to format, as a string or `Date` object.
     * @returns The formatted date in "MMMM yyyy" format.
     * @example October 2024
     */
    public static formatMonth(date: string | Date) {
        return DateFormatter.format(date, DateFormatter.monthYearFormat);
    }

    /**
     * Formats a date to a full readable format.
     * @param date - The date to format, as a string or `Date` object.
     * @returns The formatted date in "EEEE dd.MM.yyyy" format, or `null` if date is null.
     * @example Mittwoch 30.10.2024
     */
    public static formatDate(date: string | Date) {
        if (date == null) return null;
        const dateFormat = "EEEE dd.MM.yyyy";

        return DateFormatter.format(date, dateFormat);
    }

    /**
     * Formats a date to a short readable format.
     * @param date - The date to format, as a string or `Date` object, or `null`.
     * @returns The formatted date in "dd.MM.yyyy" format, or `null` if date is null.
     * @example 30.10.2024
     */
    public static formatDateShort(date: string | Date | null | undefined): string {
        if (date == null) return null;
        const dateFormat = "dd.MM.yyyy";

        return DateFormatter.format(date, dateFormat);
    }

    /**
     * Formats an ISO time string to "HH:mm" format.
     * @param time - The time string in ISO format.
     * @returns The formatted time, or `null` if time is null.
     * @example 11:16
     */
    public static formatTime(time: Date | string | undefined) {
        if (time == null) return null;
        return DateFormatter.format(time, DateFormatter.hourMinuteFormat);
    }

    /**
     * Formats an ISO time string to "HH:mm:ss" format.
     * @param time - The time string in ISO format.
     * @returns The formatted time, or `null` if time is null.
     * @example 11:16:24
     */
    public static formatTimeWithSeconds(time: Date | string | undefined) {
        if (time == null) return null;
        return DateFormatter.format(time, DateFormatter.hourMinuteSecondFormat);
    }

    /**
     * Formats a date-time or time string to "hh-mm-ss" format.
     * @param dateTime - The date-time to format, as a string or `Date` object.
     * @returns The formatted time in `hh-mm-ss` format or `null` if time is null
     * @example 13-52-02
     */
    public static formatTimeWithHyphen(dateTime: string | Date): string {
        if (dateTime == null) return null;
        return DateFormatter.format(dateTime, DateFormatter.hourMinuteSecondHyphenFormat);
    }

    /**
     * Formats a date-time to a full readable format.
     * @param dateTime - The date-time to format, as a string or `Date` object.
     * @returns The formatted date-time in "EEEE dd.MM.yyyy HH:mm:ss" format, or `null` if dateTime is null.
     * @example Mittwoch 30.10.2024 11:20:49
     */
    public static formatDateTime(dateTime: string | Date) {
        if (dateTime == null) return null;
        const dateFormat = "EEEE dd.MM.yyyy HH:mm:ss";

        return DateFormatter.format(dateTime, dateFormat);
    }

    /**
     * Formats a date-time to a shorter readable format.
     * @param dateTime - The date-time to format, as a string or `Date` object.
     * @returns The formatted date-time in "EEEE dd.MM.yyyy HH:mm" format, or `null` if dateTime is null.
     * @example Mittwoch 30.10.2024 15:45
     */
    public static formatDateTimeShort(dateTime: string | Date) {
        if (dateTime == null) return null;
        const dateFormat = "EEEE dd.MM.yyyy HH:mm";

        return DateFormatter.format(dateTime, dateFormat);
    }

    /**
     * Formats a date to "Year-Month" format for the database.
     * @param date - The date to format, as a string or `Date` object.
     * @returns The formatted date in "yyyy-MM" format.
     * @example 2024-10
     */
    public static formatDbYearMonth(date: string | Date) {
        return DateFormatter.format(date, DateFormatter.yearMonthDbFormat);
    }

    /**
     * Formats a date to "Year-Month-Day" format for the database.
     * @param date - The date to format, as a string or `Date` object.
     * @returns The formatted date in "yyyy-MM-dd" format.
     * @example 2024-10-30
     */
    public static formatDbYearMonthDate(date: string | Date) {
        return DateFormatter.format(date, DateFormatter.yearMonthDayDbFormat);
    }

    /**
     * Formats a timestamp for the database.
     * @param date - The date to format as a timestamp.
     * @returns The formatted timestamp in "yyyy-MM-ddTHH:mm:ss" format.
     * @example 2024-10-30T11:29:48
     */
    public static formatDbTimestamp(date: Date | string) {
        const formatDate = DateFormatter.format(date, DateFormatter.yearMonthDayDbFormat);
        const formatTime = DateFormatter.format(date, DateFormatter.hourMinuteSecondFormat);
        return `${formatDate}T${formatTime}`;
    }

    /**
     * Formats a date for Max. (removes the timestamp)
     * @param date - The date to format.
     * @returns The formatted date in "yyyy-MM-ddT00:00:00" format.
     * @example 2024-10-30T00:00:00
     */
    public static formatMaxDate(date: string | Date) {
        const formatDate = DateFormatter.format(date, DateFormatter.yearMonthDayDbFormat);
        return `${formatDate}T00:00:00`;
    }

    /**
     * Formats a time for Max.
     * @param date - The date to extract time from.
     * @returns The formatted time in "1899-12-30THH:mm:ss" format.
     * @example 1899-12-30T11:32:16
     */
    public static formatMaxTime(date: string | Date | null) {
        if (date == null) return null;
        const formatTime = DateFormatter.format(date, DateFormatter.hourMinuteSecondFormat);
        return `1899-12-30T${formatTime}`;
    }

    /**
     * Formats a date to "yyyy-MM-dd" format.
     * @param date - The date to format, as a string or `Date` object.
     * @returns The formatted date.
     * @example 2024-10-30
     */
    public static formatYearMonthDay(date: string | Date) {
        return DateFormatter.format(date, DateFormatter.yearMonthDayDbFormat);
    }

    /**
     * Rounds the given date to the nearest half-hour.
     * @param date - The date to round.
     * @returns A new date rounded to the nearest half-hour.
     * @example 2024-10-30T15:50:00 to 2024-10-30T16:00:00
     */
    public static roundToNearestHalfHour(date: Date) {
        const minutes = 30;
        const ms = 1000 * 60 * minutes;

        return new Date(Math.round(date.getTime() / ms) * ms);
    }

    /**
     * Removes the time component from a date.
     * @param date - The date from which to remove the time.
     * @returns A new date with the time set to 00:00:00.
     * @example 2024-10-30T15:45:00 to 2024-10-30T00:00:00
     */
    public static removeTime(date: Date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    /**
     * Merges a date and time into a single `Date` object.
     * @param date - The date to merge.
     * @param time - The time to merge.
     * @returns A new `Date` object combining the date and time.
     * @example 2024-10-30T09:05:17 and 2024-05-12T15:45:00 to 2024-10-30T15:45:00
     */
    public static mergeDates(date: Date, time: Date): Date {
        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.getHours(),
            time.getMinutes(),
            time.getSeconds(),
            time.getMilliseconds()
        );
    }

    /**
     * Parses a date string into a `Date` object.
     * @param date - The date string to parse.
     * @returns The parsed `Date` object.
     */
    public static getDateFromString(date: string | null) {
        if (date === null) {
            return "";
        }
        return parseISO(date);
    }


    /**
     * Returns an array with the minimum and maximum date values for a date picker.
     * Minimum date is current year - 2 and maximum date is current year + 5.
     * @returns An array containing the formatted minimum and maximum dates in DbTimestamp format.
     * @example
     * const [minDate, maxDate] = DateFormatter.getMinMaxDatePicker();
     * console.log(minDate); // Output: "2022-10-30T00:00:00" (assuming current year is 2024)
     * console.log(maxDate); // Output: "2029-10-30T00:00:00" (assuming current year is 2024)
     */
    public static getMinMaxDatePicker() {
        const minDateTime = DateFormatter.formatDbTimestamp(new Date("1899-01-01"));
        const maxDateTime = DateFormatter.formatDbTimestamp(addYears(new Date(), 5));

        return [minDateTime, maxDateTime];
    }

    /**
     * Checks if a date is valid.
     * @param date - The date to check.
     * @returns `true` if the date is valid, otherwise `false`.
     * @example
     * const isValid = DateFormatter.isValidDate(new Date('2024-10-30'));
     * console.log(isValid); // Output: true
     */
    public static isValidDate(date: Date): boolean {
        return date instanceof Date && !isNaN(date.getTime());
    }

    /**
     * Internal function for formatting a date.
     * Accepts either a string or `Date` object, uses the language store for localization.
     * @param date - The date to format.
     * @param dateFormat - The format to apply.
     * @private
     */
    private static format(date: string | Date, dateFormat: string) {
        try {
            const languageStore = useLanguageStore();
            const locale = languageStore.getLocale();

            if (typeof date == "string") {
                return format(parseISO(date), dateFormat, {locale: locale});
            } else if (date instanceof Date && DateFormatter.isValidDate(date)) {
                return format(date, dateFormat, {locale: locale});
            }
        } catch (error) {
            console.error("DateFormatter format error: ", error);
        }

    }

    /**
     * Converts a European date string (dd.MM.yyyy) to ISO format (yyyy-MM-dd).
     * If the date is already in ISO format or does not match the European pattern,
     * it is returned unchanged.
     *
     * @param dateStr - The input date string in "dd.MM.yyyy" format.
     * @returns The date string in ISO 8601 format ("yyyy-MM-dd").
     *
     * @example
     * DateFormatter.convertToIso("09.04.2025"); // returns "2025-04-09"
     */
    public static convertToIso(dateStr: string): string {
        const europeanRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
        const match = europeanRegex.exec(dateStr);
        if (match) {
            const [, day, month, year] = match;
            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }
        return dateStr;
    }
}

export default DateFormatter;
